import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import BlogList from './components/BlogList';
import BlogPage from './components/pages/BlogPage';
import SpherePage from './components/pages/SpherePage';
import NextILandingPage from './components/pages/nexti_landingpage';
import { Global } from '@emotion/react';
import globalStyles from './styles/GlobalStyles';

/**
 * URLのパスパラメータに基づいて BlogPage または SpherePage を表示するラッパーコンポーネント。
 */
const IdRouteWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNumericId = id ? /^\d+$/.test(id) : false;

  return isNumericId ? <BlogPage /> : <SpherePage />;
};

/**
 * パス名に基づいて、ページの「種類」を一意に表すキーを返す。
 * これにより、同じ種類のページ間での遷移時に不要な再マウントを防ぐ。
 * @param pathname - location.pathname
 * @returns ページの種類を表すキー文字列
 */
const getPageKey = (pathname: string): string => {
  // ブログ詳細ページ（例: /123）
  if (/^\/\d+$/.test(pathname)) {
    return 'blog-detail';
  }
  // ブログ一覧ページ
  if (pathname.startsWith('/blog')) {
    return 'blog-list';
  }
  // Nexti関連ページ
  if (pathname.startsWith('/nexti')) {
    return 'nexti';
  }
  // 上記以外はすべてSpherePageとして扱う
  return 'sphere';
};

const App: React.FC = () => {
  const location = useLocation();
  const pageKey = getPageKey(location.pathname);

  // 各ページタイプごとのアニメーション設定
  const pageAnimations = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.5 },
  };

  return (
    <>
      <Global styles={globalStyles} />
      <AnimatePresence mode="wait">
        <motion.div key={pageKey} {...pageAnimations}>
          <Routes location={location}>
            <Route
              path="/blog"
              element={<BlogList />}
            />
            <Route
              path="/nexti"
              element={<NextILandingPage />}
            />
            <Route
              path="/nexti_landingpage"
              element={<NextILandingPage />}
            />
            <Route path="/:id" element={<IdRouteWrapper />} />
            <Route path="*" element={<SpherePage />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

const AppWrapper: React.FC = () => {
  return (
    <Router>
      <App />
    </Router>
  );
};

export default AppWrapper;
